// src/components/course/BuyCourseModal.jsx

import React, { useEffect, useRef, useState } from "react";
import api from "../../api/api";
import { useCart } from "../../contexts/CartContext";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = "https://imagensdaface.com.br";

/* ===============================
   NORMALIZA URL DO VÍDEO
================================ */
const resolveVideoUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${BACKEND_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

export default function BuyCourseModal({ course, onClose }) {
  const [preview, setPreview] = useState(null);
  const [settings, setSettings] = useState(null);
  const [phase, setPhase] = useState("intro"); // intro | playing | ended | no-video
  const [remaining, setRemaining] = useState(0);

  const videoRef = useRef(null);
  const countdownTimer = useRef(null);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  // --- LÓGICA DE PERMISSÃO (ADMIN/SUPERVISOR/PROFESSOR) ---
  const userStored = localStorage.getItem("user");
  const user = userStored ? JSON.parse(userStored) : null;
  const isPrivileged = user && ["admin", "supervisor", "professor"].includes(user.role);

  /* ===============================
      LOAD PREVIEW + SETTINGS
  ================================ */
  useEffect(() => {
    if (!course?.id) return;

    (async () => {
      try {
        const [previewRes, settingsRes] = await Promise.all([
          api.get(`/courses/${course.id}/preview`),
          api.get("/global/settings"),
        ]);

        // previewRes.data.preview agora virá direto de Courses
        const previewData = previewRes.data.preview;
        
        setPreview(previewData || null);
        setSettings(settingsRes.data.settings || null);

        // Se o preview for nulo ou não tiver a URL (content), muda para fase no-video
        if (!previewData || !previewData.content) {
          setPhase("no-video");
        } else {
          setPhase("intro"); // Garante que volta para a intro se houver vídeo
        }
      } catch (err) {
        console.error("Erro ao carregar preview", err);
        setPhase("no-video");
      }
    })();

    return () => clearInterval(countdownTimer.current);
  }, [course?.id]);
  
  
  /* ===============================
      EFEITO PARA DAR PLAY
  ================================ */
  useEffect(() => {
    if (phase === "playing" && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(err => {
        console.warn("Autoplay bloqueado ou erro no vídeo:", err);
      });
    }
  }, [phase]);

  /* ===============================
      START VIDEO
  ================================ */
  const handleStartPreview = () => {
    if (!preview?.content) return;

    const seconds = Number(preview.preview_seconds) || 10;
    setRemaining(seconds);
    setPhase("playing");

    // Limpa timer anterior se existir
    if (countdownTimer.current) clearInterval(countdownTimer.current);

    countdownTimer.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          finishPreview();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const finishPreview = () => {
    clearInterval(countdownTimer.current);
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setPhase("ended");
  };

  const handleAddToCart = () => {
    addToCart(course);
    navigate("/checkout");
  };

  // Redireciona Admins direto para o player/área do curso
  const handleAccessCourse = () => {
    navigate(`/courses/${course.id}/learn`);
    // onClose(); // Opcional: fechar modal, mas o navigate geralmente desmonta o componente
  };

  const logoUrl = settings?.SITE_LOGO
    ? settings.SITE_LOGO.startsWith("http")
      ? settings.SITE_LOGO
      : `${BACKEND_URL}${settings.SITE_LOGO}`
    : null;

  if (!course) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-6xl w-full overflow-hidden shadow-2xl flex flex-col md:flex-row relative">
        
        {/* FECHAR MODAL */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 text-white/50 hover:text-white text-2xl bg-black/20 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
        >
          ✕
        </button>

        {/* ================= LADO ESQUERDO: VÍDEO ================= */}
        <div className="relative w-full md:w-3/5 min-h-[300px] md:min-h-[420px] bg-black flex items-center justify-center">
          
          {/* O Vídeo fica sempre no DOM se houver preview, mas invisível se não estiver 'playing' */}
          {preview?.content && (
            <div className={`w-full h-full ${phase === "playing" ? "block" : "hidden"}`}>
              <video
                ref={videoRef}
                src={resolveVideoUrl(preview.content)}
                className="w-full h-full object-cover"
                playsInline
                muted // Muted é obrigatório para garantir o play automático
                onEnded={finishPreview}
              />
              
              {/* CONTADOR */}
              <div className="absolute top-4 left-4 bg-black/70 text-white px-4 py-2 rounded-full font-black animate-pulse z-10">
                ⏱ {remaining}s
              </div>
            </div>
          )}

          {/* OVERLAY DE INTRO / FIM / ERRO */}
          {(phase === "intro" || phase === "ended" || phase === "no-video") && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/40 backdrop-blur-sm animate-fade-in p-6 text-center">
              
              {logoUrl && phase !== "no-video" && (
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="mb-8 animate-pulse w-48 md:w-64"
                  style={{
                    filter: `drop-shadow(0 0 30px ${settings?.SITE_LOGO_SHADOW_COLOR || "#6366f1"})`,
                  }}
                />
              )}

              {phase === "intro" && (
                <button
                  onClick={handleStartPreview}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-2xl font-black text-xl shadow-2xl transition-all hover:scale-105 active:scale-95"
                >
                  ▶ Iniciar Degustação
                </button>
              )}

              {phase === "ended" && (
                <div className="flex flex-col items-center">
                  <h4 className="text-white text-2xl font-bold mb-4">Gostou do que viu?</h4>
                  
                  {isPrivileged ? (
                    <button
                        onClick={handleAccessCourse}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white px-10 py-4 rounded-2xl font-black text-xl shadow-2xl transition-all hover:scale-105"
                    >
                        🔓 Acessar (Admin)
                    </button>
                  ) : (
                    <button
                        onClick={handleAddToCart}
                        className="bg-green-600 hover:bg-green-500 text-white px-10 py-4 rounded-2xl font-black text-xl shadow-2xl transition-all hover:scale-105"
                    >
                        Comprar Curso Completo
                    </button>
                  )}
                </div>
              )}

              {phase === "no-video" && (
                <div className="text-white opacity-60">
                  <p>Vídeo de demonstração indisponível.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ================= LADO DIREITO: INFO ================= */}
        <div className="w-full md:w-2/5 p-8 md:p-10 flex flex-col justify-between bg-slate-900 border-t md:border-t-0 md:border-l border-slate-800">
          <div>
            <h3 className="text-3xl font-black text-white mb-3">
              {course.title}
            </h3>

            <p className="text-slate-400 text-sm mb-6 leading-relaxed">
              {course.description}
            </p>

            {preview?.preview_seconds && (
              <div className="inline-block bg-indigo-500/10 text-indigo-400 text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-widest border border-indigo-500/20">
                Amostra Grátis: {preview.preview_seconds}s
              </div>
            )}
          </div>

          <div className="mt-8">
            <div className="flex flex-col mb-6">
              <span className="text-slate-500 text-sm font-bold uppercase tracking-tighter">Preço do Investimento</span>
              <p className="text-white text-5xl font-black">
                R$ {Number(course.price).toFixed(2)}
              </p>
            </div>

            {isPrivileged ? (
                <button
                    onClick={handleAccessCourse}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-5 rounded-2xl font-black text-xl shadow-xl transition-all hover:shadow-emerald-500/20"
                >
                    🔓 Acessar (Admin)
                </button>
            ) : (
                <button
                    onClick={handleAddToCart}
                    className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-5 rounded-2xl font-black text-xl shadow-xl transition-all hover:shadow-indigo-500/20"
                >
                    Adicionar ao Carrinho
                </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}